import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, ArrowLeft } from 'lucide-react';

const Privacy = () => {
    return (
        <div className="min-h-screen bg-hero">
            {/* Header */}
            <section className="py-12 px-6 bg-gradient-to-b from-gray-900/80 to-transparent">
                <div className="max-w-4xl mx-auto">
                    <Link 
                        to="/"
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-300 mb-8"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Home
                    </Link>
                    
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-6">
                            <Shield className="w-16 h-16 text-blue-400" />
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black gradient-text mb-6">
                            Privacy Policy
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Your privacy matters to us. Learn how we protect and handle your data on The Deep Wave platform.
                        </p>
                        <p className="text-sm text-gray-500 mt-4">
                            Last updated: January 2024
                        </p>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-12">
                        
                        {/* Information We Collect */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Database className="w-8 h-8 text-blue-400" />
                                <h2 className="text-2xl font-bold gradient-text">Information We Collect</h2>
                            </div>
                            <div className="space-y-6 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-3">Personal Information</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• Email address and username when you create an account</li>
                                        <li>• Profile information including bio, display name, and location</li>
                                        <li>• Artist information if you enable artist status (genres, social media links)</li>
                                        <li>• Payment information for premium features (processed securely)</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-3">Content Data</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• Audio, video, and image files you upload</li>
                                        <li>• Posts, comments, and social interactions</li>
                                        <li>• Metadata associated with your media files</li>
                                        <li>• Event information you create or participate in</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-3">Usage Analytics</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• Play counts, view statistics, and engagement metrics</li>
                                        <li>• Session data and platform usage patterns</li>
                                        <li>• Device information and browser data</li>
                                        <li>• IP address and general location data</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* How We Use Your Information */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <UserCheck className="w-8 h-8 text-green-400" />
                                <h2 className="text-2xl font-bold gradient-text">How We Use Your Information</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Platform Operations</h3>
                                    <p className="text-gray-400">
                                        We use your information to provide core platform services, including account management, 
                                        content delivery, and community features. This enables you to upload media, connect with 
                                        other artists, and participate in events.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Content Discovery</h3>
                                    <p className="text-gray-400">
                                        Analytics data helps us improve content recommendations, showcase featured artists, 
                                        and create better discovery experiences for the entire community.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Communication</h3>
                                    <p className="text-gray-400">
                                        We may send you important platform updates, event notifications, and community 
                                        announcements. You can control these preferences in your account settings.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Security & Safety</h3>
                                    <p className="text-gray-400">
                                        Your data helps us maintain platform security, prevent abuse, and ensure a safe 
                                        creative environment for all community members.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Data Protection */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Lock className="w-8 h-8 text-yellow-400" />
                                <h2 className="text-2xl font-bold gradient-text">Data Protection & Security</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Encryption</h3>
                                    <p className="text-gray-400">
                                        All data transmission uses SSL/HTTPS encryption. User passwords are securely hashed 
                                        using bcrypt, and sensitive data is encrypted at rest.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Access Controls</h3>
                                    <p className="text-gray-400">
                                        We implement strict access controls and authentication systems. Only authorized 
                                        personnel have access to user data, and all access is logged and monitored.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Data Backups</h3>
                                    <p className="text-gray-400">
                                        Regular automated backups ensure your content and data are protected against loss 
                                        or corruption, with multiple redundancy layers.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Data Sharing */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Eye className="w-8 h-8 text-purple-400" />
                                <h2 className="text-2xl font-bold gradient-text">Data Sharing & Third Parties</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">What We Don't Share</h3>
                                    <p className="text-gray-400">
                                        We never sell your personal information to third parties. Your private content, 
                                        personal messages, and account details remain confidential.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Public Content</h3>
                                    <p className="text-gray-400">
                                        Content you choose to make public (profile information, uploaded media, posts) 
                                        is visible to other platform users and may be indexed by search engines.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Service Providers</h3>
                                    <p className="text-gray-400">
                                        We work with trusted service providers for hosting, analytics, and payment processing. 
                                        These partners are bound by strict privacy agreements and security standards.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Legal Requirements</h3>
                                    <p className="text-gray-400">
                                        We may disclose information when required by law, to protect our rights, or to ensure 
                                        the safety and security of our users and the platform.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Your Rights */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <UserCheck className="w-8 h-8 text-cyan-400" />
                                <h2 className="text-2xl font-bold gradient-text">Your Rights & Controls</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Account Access</h3>
                                    <p className="text-gray-400">
                                        You can view, edit, and delete your profile information at any time through your 
                                        account settings page.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Content Control</h3>
                                    <p className="text-gray-400">
                                        You have full control over your uploaded content. You can edit metadata, change 
                                        privacy settings, or delete content permanently at any time.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Data Export</h3>
                                    <p className="text-gray-400">
                                        Upon request, we can provide you with a copy of your personal data in a portable format.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Account Deletion</h3>
                                    <p className="text-gray-400">
                                        You can permanently delete your account and associated data at any time. This action 
                                        cannot be undone, and all your content will be removed from the platform.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Cookies & Analytics */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Database className="w-8 h-8 text-red-400" />
                                <h2 className="text-2xl font-bold gradient-text">Cookies & Analytics</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Essential Cookies</h3>
                                    <p className="text-gray-400">
                                        We use essential cookies for authentication, session management, and core platform 
                                        functionality. These cannot be disabled without affecting platform operation.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Analytics Cookies</h3>
                                    <p className="text-gray-400">
                                        We use Google Analytics and internal analytics to understand how users interact 
                                        with the platform, helping us improve the user experience.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Media Tracking</h3>
                                    <p className="text-gray-400">
                                        We track play counts, view statistics, and engagement metrics to provide accurate 
                                        analytics to content creators and improve content discovery.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Updates & Contact */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Shield className="w-8 h-8 text-blue-400" />
                                <h2 className="text-2xl font-bold gradient-text">Policy Updates & Contact</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Policy Changes</h3>
                                    <p className="text-gray-400">
                                        We may update this privacy policy from time to time. Significant changes will be 
                                        communicated to users via email and platform notifications.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Questions & Concerns</h3>
                                    <p className="text-gray-400">
                                        If you have questions about this privacy policy or concerns about how we handle 
                                        your data, please contact our support team.
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                    <Link 
                                        to="/support" 
                                        className="btn-primary flex items-center gap-2 justify-center"
                                    >
                                        <Shield className="w-5 h-5" />
                                        Contact Support
                                    </Link>
                                    <Link 
                                        to="/terms" 
                                        className="btn-ghost flex items-center gap-2 justify-center"
                                    >
                                        View Terms of Service
                                    </Link>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default Privacy;